import { useMutation } from '@src/query';


/**
 * M27 AI photo-intake scan mutations (#447 / #448, consumed by #449 / #450).
 *
 * Mirrors `usePartFormMutations.js`: one `query/<area>/use<Thing>Mutations.js`
 * module re-exported through the `@src/query` barrel, wrapping the shared
 * `useMutation` engine with the injected-`queryClient`
 * `mutateFn({ apiClient, queryClient })` contract.
 *
 * There is deliberately no "poll" hook here — the scan poll is a plain
 * `useQuery` with a `refetchInterval` driven by the pure `scanPollInterval`
 * helper, exactly as M14's deep analysis does it.
 */


/**
 * Start a scan: multipart upload of 1..12 photos → 202 `{ id, status, created_at }`.
 * Variables: `{ sellerId, files }`.
 *
 * The photos are uploaded UNCOMPRESSED, unlike the Add-Part staged-photo path
 * (`usePartSaveMutation` runs them through `compressImage` first). That is not an
 * oversight: this is an OCR pipeline reading small printed part numbers off a
 * label, and the 1600px/q0.82 re-encode is exactly the detail it needs. The same
 * files become the draft's photos server-side when create-draft runs, so nothing
 * is uploaded twice.
 *
 * No cache invalidation — a scan creates no inventory (that is create-draft's
 * job, and the whole no-auto-create invariant).
 */
function useScanStartMutation(params) {
  return useMutation({
    mutateFn: ({ apiClient, sellerId, files }) => {
      const body = new FormData();
      (files || []).forEach((file) => body.append('images', file));
      return apiClient.request({
        url: `/inventory/parts/${sellerId}/scans/`,
        method: 'POST',
        payload: body,
        contentType: 'multipart/form-data',
      });
    },
    ...params,
  });
}


/**
 * Turn a confirmed scan into a DRAFT part → 201 `{ part }`. Variables:
 * `{ sellerId, scanId, payload, keyPrefix }` where `payload` is built by the pure
 * `buildCreateDraftPayload` helper from the Gate 1 identity + accepted Gate 2 chips.
 *
 * This is the ONLY call in the photo-intake flow that creates anything, so it is
 * also the only one that invalidates the parts list.
 */
function useScanCreateDraftMutation(params) {
  return useMutation({
    mutateFn: async ({ apiClient, queryClient, sellerId, scanId, payload, keyPrefix }) => {
      const created = await apiClient.request({
        url: `/inventory/parts/${sellerId}/scans/${scanId}/create-draft/`,
        method: 'POST',
        payload,
      });
      if (keyPrefix) {
        queryClient.invalidateQueries({ queryKey: keyPrefix });
      }
      return created;
    },
    ...params,
  });
}


export { useScanCreateDraftMutation, useScanStartMutation };
